import React, { FC, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Modal, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';

import { Theme } from '@constants';

import { styles } from './styles';

const tPath = 'locations.';
const animationDuration = 300;
const { drawerModalBackground } = Theme.gradients;
const gradientColors = [drawerModalBackground.start, drawerModalBackground.end];

type SideDrawerModalProps = {
  visible: boolean;
  handleClose: () => void;
  children: ReactNode;
};

export const SideDrawerModal: FC<SideDrawerModalProps> = ({ visible, handleClose, children }) => {
  const { t } = useTranslation();

  const animation = useRef(new Animated.Value(0)).current;

  const animateIn = (onComplete?: () => void) => {
    Animated.timing(animation, {
      toValue: 1,
      duration: animationDuration,
      useNativeDriver: true,
    }).start(onComplete);
  };

  const animateOut = (onComplete?: () => void) => {
    Animated.timing(animation, {
      toValue: 0,
      duration: animationDuration,
      useNativeDriver: true,
    }).start(onComplete);
  };

  const modalShown = () => {
    animateIn();
  };

  const close = () => {
    animateOut(handleClose);
  };

  return (
    <Modal
      accessibilityHint={t(`${tPath}modalAccessibilityHint`)}
      accessibilityLabel={t(`${tPath}modalAccessibilityLabel`)}
      accessible
      animationType={'none'}
      autoFocus={false}
      transparent
      visible={visible}
      onRequestClose={close}
      onShow={modalShown}
    >
      <View style={styles.modalScreen}>
        <Animated.View
          style={[
            styles.backdrop,
            {
              opacity: animation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 0.8],
              }),
            },
          ]}
        />
        <Animated.View
          style={[
            styles.panel,
            {
              transform: [
                {
                  translateX: animation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [Theme.appWidth, Theme.appWidth - Theme.sidePanelWidth],
                  }),
                },
              ],
            },
          ]}
        >
          <LinearGradient colors={gradientColors} style={styles.panelGradient}>
            <View style={styles.contentWrapper}>{children}</View>
          </LinearGradient>
        </Animated.View>
      </View>
    </Modal>
  );
};
